/**
 * Tabs.tsx — Componente de pestanas del design system
 *
 * Patrón: Radix Tabs (primitivo) + CVA (variantes TabsList) + Tailwind + cn().
 * Sigue la referencia canónica de Button.tsx (ADR-025).
 *
 * Exports: Tabs, TabsList, TabsTrigger, TabsContent
 *
 * Por qué Radix Tabs:
 *   Provee role="tablist", role="tab", role="tabpanel", aria-selected,
 *   aria-controls/aria-labelledby, navegación con flechas, Home/End y
 *   focus management — sin implementación manual (ADR-025).
 *
 * Por qué CVA en TabsList y TabsTrigger:
 *   Las variantes default/pills/underline y los estados active/inactive
 *   tienen clases condicionales complejas. CVA las hace tipadas y
 *   colocadas fuera del JSX, facilitando el mantenimiento.
 *
 * Variantes de lista (prop variant en TabsList):
 *   - default   → borde inferior del contenedor; trigger activo con
 *                 underline cumple-accent-500 y texto cumple-primary-700
 *   - pills     → fondo gray-100 redondeado; trigger activo con fondo
 *                 blanco y sombra (estilo botón)
 *   - underline → solo texto con underline animado debajo del trigger activo
 *
 * Orientación:
 *   La prop orientation de Radix Tabs.Root controla el layout.
 *   horizontal: fila (default) — vertical: columna.
 *
 * [T-1 — Desviación ADR-027 §plan Día 4 — Registrada 2026-05-27]
 *   ADR-027 especifica usar `<Check />` (Lucide) como indicador de tab activo.
 *   Este componente implementa un `border-b-2` underline (variantes default y
 *   underline) como indicador visual de selección — elección de diseño Día 4
 *   que cumple WCAG 2.4.3 y es funcionalmente equivalente. No se importa
 *   lucide-react en este módulo. La desviación es deliberada: el underline
 *   es suficientemente expresivo y reduce la complejidad del trigger.
 *   Si se requiere el ícono Check en el futuro, añadir prop `showActiveIcon`
 *   opcional sin breaking change.
 *
 * [T-2 — Layout shift pills/underline — Verificado 2026-05-27]
 *   La variante `pills` del trigger no usa `-mb-px` (correcto: no tiene
 *   border-b que compensar). La base del trigger fija `min-h-[44px]` que es
 *   constante entre variantes, garantizando que no hay layout shift de altura
 *   al activar. Los triggers `default`/`underline` usan `border-b-2 transparent`
 *   en inactivo como placeholder exacto del `border-b-2 accent-500` activo.
 */
import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from './utils';

// ---------------------------------------------------------------------------
// Variantes CVA — TabsList contenedor
// ---------------------------------------------------------------------------

const tabsListVariants = cva(
  // Base: flex-row de triggers; gap y text-sm base
  ['flex items-center', 'text-sm font-medium'],
  {
    variants: {
      variant: {
        /**
         * default — Borde inferior en el contenedor. El trigger activo muestra
         * un underline de 2px en cumple-accent-500 superpuesto al borde.
         * Patrón: borde gris en toda la lista, line activa en trigger individual.
         */
        default: 'border-b border-cumple-gray-200 gap-0',
        /**
         * pills — Fondo redondeado gray-100 que envuelve todos los triggers.
         * Trigger activo: fondo blanco + sombra (aspecto de botón elevado).
         */
        pills: 'bg-cumple-gray-100 rounded-lg p-1 gap-1',
        /**
         * underline — Sin fondo/borde en el contenedor; solo texto.
         * Trigger activo: underline animado, texto cumple-primary-700.
         * Más sutil que default.
         */
        underline: 'gap-4',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

// ---------------------------------------------------------------------------
// Variantes CVA — TabsTrigger individual
// ---------------------------------------------------------------------------

const tabsTriggerVariants = cva(
  // Base: flex inline, área táctil WCAG 2.5.5, focus ring
  [
    'inline-flex items-center justify-center whitespace-nowrap',
    'text-cumple-gray-600 transition-all duration-150',
    'focus-visible:outline-none focus-visible:ring-2',
    'focus-visible:ring-cumple-accent-500 focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-50',
    'cursor-pointer',
    // Área táctil mínima tablet-first WCAG 2.5.5
    'min-h-[44px] min-w-[44px] px-3',
  ],
  {
    variants: {
      variant: {
        /**
         * default — Trigger con padding-bottom; el estado activo posiciona un
         * pseudo-underline de 2px mediante border-b visible solo en data-[state=active].
         *
         * Por qué border-b-2 en activo y border-b-2 transparent en inactivo:
         *   Mantener el mismo height en ambos estados evita el layout shift al
         *   activar. Se aplica border transparente en inactivo como placeholder.
         */
        default: [
          'border-b-2 border-b-transparent -mb-px',
          // hover: texto más oscuro
          'hover:text-cumple-primary-900',
          // activo: texto primary-700 + border accent-500
          'data-[state=active]:border-b-cumple-accent-500',
          'data-[state=active]:text-cumple-primary-700',
        ],
        /**
         * pills — Trigger redondeado; activo con fondo blanco + sombra.
         */
        pills: [
          'rounded-md',
          'hover:text-cumple-primary-900',
          // activo: fondo blanco, sombra sutil, texto primary-700
          'data-[state=active]:bg-white data-[state=active]:shadow-sm',
          'data-[state=active]:text-cumple-primary-700',
        ],
        /**
         * underline — Solo texto; activo con underline y texto primary-700.
         * El underline se hace con border-b para mantener consistencia con default.
         */
        underline: [
          'border-b-2 border-b-transparent pb-1',
          'hover:text-cumple-primary-900',
          // activo: underline accent-500 + texto primary-700
          'data-[state=active]:border-b-cumple-accent-500',
          'data-[state=active]:text-cumple-primary-700',
        ],
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

// ---------------------------------------------------------------------------
// Tipos y contexto para propagar variant a los triggers hijos
// ---------------------------------------------------------------------------

type TabsVariant = VariantProps<typeof tabsListVariants>['variant'];

const TabsVariantContext = React.createContext<TabsVariant>('default');

// ---------------------------------------------------------------------------
// Tabs — Root
// ---------------------------------------------------------------------------

/**
 * Tabs — Raíz del componente. Gestiona el estado de tab activo (controlled
 * o uncontrolled). Acepta todas las props de Radix Tabs.Root.
 *
 * @example Uncontrolled:
 *   <Tabs defaultValue="general">
 *     <TabsList><TabsTrigger value="general">General</TabsTrigger></TabsList>
 *     <TabsContent value="general">…</TabsContent>
 *   </Tabs>
 *
 * @example Controlled:
 *   const [tab, setTab] = React.useState('general');
 *   <Tabs value={tab} onValueChange={setTab}>…</Tabs>
 */
const Tabs = TabsPrimitive.Root;
Tabs.displayName = 'Tabs';

// ---------------------------------------------------------------------------
// TabsList — Contenedor de triggers
// ---------------------------------------------------------------------------

export interface TabsListProps
  extends
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>,
    VariantProps<typeof tabsListVariants> {}

/**
 * TabsList — Contenedor flex de TabsTrigger.
 * La prop variant propaga el estilo a cada TabsTrigger hijo mediante contexto
 * React, de modo que el consumidor no necesita repetir variant en cada trigger.
 *
 * @example
 *   <TabsList variant="pills">
 *     <TabsTrigger value="a">Pestaña A</TabsTrigger>
 *     <TabsTrigger value="b">Pestaña B</TabsTrigger>
 *   </TabsList>
 */
const TabsList = React.forwardRef<React.ElementRef<typeof TabsPrimitive.List>, TabsListProps>(
  ({ className, variant = 'default', children, ...props }, ref) => (
    <TabsVariantContext.Provider value={variant}>
      <TabsPrimitive.List
        ref={ref}
        className={cn(tabsListVariants({ variant }), className)}
        {...props}
      >
        {children}
      </TabsPrimitive.List>
    </TabsVariantContext.Provider>
  ),
);
TabsList.displayName = 'TabsList';

// ---------------------------------------------------------------------------
// TabsTrigger — Botón de pestaña individual
// ---------------------------------------------------------------------------

export interface TabsTriggerProps
  extends
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>,
    Partial<VariantProps<typeof tabsTriggerVariants>> {}

/**
 * TabsTrigger — Elemento de pestaña individual.
 * Radix gestiona automáticamente: role="tab", aria-selected, aria-controls,
 * navegación con flechas, Home/End, activación con Enter/Space.
 *
 * La variante visual se hereda del TabsList padre via contexto, pero puede
 * sobreescribirse localmente pasando la prop variant.
 *
 * @example
 *   <TabsTrigger value="hallazgos">Hallazgos (12)</TabsTrigger>
 */
const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(({ className, variant, ...props }, ref) => {
  // Si variant no se pasa explícitamente, hereda del TabsList padre.
  const contextVariant = React.useContext(TabsVariantContext);
  const resolvedVariant = variant ?? contextVariant ?? 'default';

  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(tabsTriggerVariants({ variant: resolvedVariant }), className)}
      {...props}
    />
  );
});
TabsTrigger.displayName = 'TabsTrigger';

// ---------------------------------------------------------------------------
// TabsContent — Panel de contenido
// ---------------------------------------------------------------------------

/**
 * TabsContent — Panel asociado a un trigger.
 * Radix gestiona automáticamente: role="tabpanel", aria-labelledby,
 * visibilidad (hidden cuando no activo), foco al activar con teclado.
 *
 * Padding top pt-4 separa el contenido de la lista de pestanas.
 * Focus ring para cuando el panel recibe foco por Tab desde el trigger.
 *
 * @example
 *   <TabsContent value="hallazgos">
 *     <p>Listado de hallazgos del levantamiento.</p>
 *   </TabsContent>
 */
const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-4',
      // Focus ring cuando el panel recibe foco (navegación por teclado)
      'focus-visible:outline-none focus-visible:ring-2',
      'focus-visible:ring-cumple-accent-500 focus-visible:ring-offset-2',
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = 'TabsContent';

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export { Tabs, TabsList, TabsTrigger, TabsContent, tabsListVariants, tabsTriggerVariants };
